import { type GroupSummary } from '@fs/zion-tree-types'
import { useCacheService } from '@fs/zion-cache-service'
import { cacheOld, cacheServiceOld } from './caches'
import { type GroupsResponse, type GedcomFile, type GedcomFileToGroupSummary, type GedcomStatus } from './groupTypes'

/**
 * Gets a list of summaries (name, id, displayName) for each of the user's groups.
 * @throws 400 - The Group id wasn't valid.
 * @throws 403 - Only un-banished group members can view a group.
 * @throws 404 - Group not found.
 * @throws 410 - Group is tombstoned.
 */
export default function getGroupsList(resetCache?: boolean): Promise<GroupSummary[]> {
  if (resetCache) {
    cacheOld.removeItem('group-list')
  }
  return cacheServiceOld
    .get<GroupsResponse>(`/service/tree/tree-data/groups/current-groups`, 'group-list')
    .then((res) => res.memberSummaries)
}

export const useGroupsList = (): ReturnType<typeof useCacheService<GroupsResponse>> => {
  return useCacheService<GroupsResponse>(cacheServiceOld, `/service/tree/tree-data/groups/current-groups`, 'group-list')
}

function filterGroupsByType(groups: GroupSummary[], isFamily?: boolean): GroupSummary[] {
  return groups.filter(({ types }) =>
    isFamily ? !types.includes('RESEARCHER_TREE') : types.includes('RESEARCHER_TREE')
  )
}

function isFailedFile(fileStatus: string, treeId?: string | null): boolean {
  return (
    fileStatus === 'FAILED_UPLOAD' || fileStatus === 'FAILED_PROCESSING' || (fileStatus === 'FAILED_IMPORT' && !treeId)
  )
}

function shouldIncludeFailedFile(file: GedcomFile, groups: GroupSummary[]): boolean {
  const { fileStatus, treeId, groupId } = file

  // For researcher groups, show failures and processing states
  return Boolean(
    isFailedFile(fileStatus, treeId) ||
    (isInProcessingState(fileStatus) && !treeId) ||
    (isInProcessingState(fileStatus) && treeId && !groups?.some(({ id }) => id === groupId))
  )
}

function convertFilesToGroups(
  gedcomFileList: GedcomFile[] | undefined,
  isFamily: boolean | undefined,
  groups: GroupSummary[]
): GedcomFileToGroupSummary[] {
  if (!gedcomFileList || isFamily) {
    return []
  }

  return gedcomFileList
    .filter((file) => shouldIncludeFailedFile(file, groups))
    .map(
      (file) =>
        ({
          file,
          name: file.title,
          fileStatus: file.fileStatus,
          isFailed: true,
          isProcessing: isInProcessingState(file.fileStatus),
          types: [],
          id: file.groupId ?? undefined,
          imagePending: false,
          memberStatus: 'MANAGER',
          preferred: false,
        }) satisfies GedcomFileToGroupSummary
    )
}

function enhanceTreesWithFileState(group: GroupSummary, gedcomFileList: undefined, isFamily: true): GroupSummary
function enhanceTreesWithFileState(
  group: GedcomFileToGroupSummary | GroupSummary,
  gedcomFileList: GedcomFile[] | undefined,
  isFamily: false | undefined
): GedcomFileToGroupSummary
function enhanceTreesWithFileState(
  group: GedcomFileToGroupSummary | GroupSummary,
  gedcomFileList: GedcomFile[] | undefined,
  isFamily: boolean | undefined
): GroupSummary | GedcomFileToGroupSummary
function enhanceTreesWithFileState(
  group: GedcomFileToGroupSummary | GroupSummary,
  gedcomFileList: GedcomFile[] | undefined,
  isFamily: boolean | undefined
): GroupSummary | GedcomFileToGroupSummary {
  // Family groups don't need file state updates
  if (isFamily) {
    return group
  }

  const fileAssociatedWithTree = gedcomFileList?.find(({ groupId }) => groupId === group.id)

  if ('isProcessing' in group) {
    return {
      ...group,
      isProcessing: group.isProcessing || fileAssociatedWithTree?.fileStatus === 'IMPORTING',
      needToRefetchGroups: !fileAssociatedWithTree,
    }
  }

  return group
}

type SortAndFilterGroupsParams = {
  groups: GroupSummary[]
  isFamily?: boolean
  gedcomFileList?: GedcomFile[]
}

export function sortAndFilterGroups(params: SortAndFilterGroupsParams & { isFamily: true }): GroupSummary[]
export function sortAndFilterGroups(
  params: SortAndFilterGroupsParams & { isFamily?: false }
): (GroupSummary | GedcomFileToGroupSummary)[]
export function sortAndFilterGroups(
  params: SortAndFilterGroupsParams
): GroupSummary[] | (GroupSummary | GedcomFileToGroupSummary)[]
export function sortAndFilterGroups({
  groups,
  isFamily,
  gedcomFileList,
}: SortAndFilterGroupsParams): GroupSummary[] | (GroupSummary | GedcomFileToGroupSummary)[] {
  if (isFamily) {
    const filteredGroups = filterGroupsByType(groups, isFamily)
    return filteredGroups.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()))
  }

  const filteredGroups = filterGroupsByType(groups, isFamily)
  const fileGroups = convertFilesToGroups(gedcomFileList, isFamily, groups)
  const allGroups: (GroupSummary | GedcomFileToGroupSummary)[] = [...filteredGroups, ...fileGroups]
  const enhancedGroups = allGroups.map((group) => enhanceTreesWithFileState(group, gedcomFileList, isFamily))
  return enhancedGroups.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()))
}

function isInProcessingState(fileStatus: GedcomStatus): boolean {
  // Of these file statuses IMPORTING is the only one that will have a treeId
  return new Set<GedcomStatus>(['IMPORTING', 'UPLOADING', 'PROCESSING', 'PROCESSED']).has(fileStatus)
}
